// Single source of truth for push-adapter frontend metadata.
//
// Each entry in ADAPTER_PROFILES declares one merchant adapter (matching a
// backend push adapter_type). The map drives:
//   - the create/update <select> options on /push-sites (ADAPTER_CHOICES),
//   - the per-merchant test drawer on /push-sites (endpoints, default
//     bodies/queries, auth header line),
//   - and the /push-bindings form's capability-gated controls (does this
//     merchant expose a "remote templates" list — `hasRemoteTemplates`).
//
// Adding a merchant = one entry here + one backend adapter registration; the
// UI picks up the option, the drawer, and the binding-form behavior with no
// hand-maintained lists to keep in sync.

export type Method = "GET" | "POST" | "PUT" | "DELETE";

export type EzEp = {
  idx: number;
  m: Method;
  path: string;
  hasBody: boolean;
  hasQuery: boolean;
  note: string;
  mutating?: boolean;
};

// FieldMappingKeySpec declares how one field_mapping key is understood by a
// merchant adapter (doc/041 第一批 + doc/043 三要素). It drives the binding-UI
// whitelist hints and the pre-save validation: which keys are allowed, which
// are required, and what values they take (enum/int/string + hint). The allowed
// key set mirrors the backend adapters' allowedFieldMappingKeys — see the
// per-adapter pointer in each profile. `required: true` keys are the same set
// the backend enforces at CreateChannel pre-flight ("未发送:… is required").
//
// 三要素分工(doc/043 §3.1):
//   - meaning  — 含义:这个 key 控制上游的什么行为
//   - useCase  — 适用场景:何时/什么条件下需要填、对分发行为的影响
//   - hint     — 输入格式 / 默认值 / 取值来源(维持第一批语义)
export type FieldMappingKeySpec = {
  key: string;
  label: string;
  required?: boolean;
  type?: "string" | "int" | "decimal" | "enum";
  enum?: string[];
  enumLabels?: Record<string, string>;
  visibleByDefault?: boolean;
  meaning?: string;
  useCase?: string;
  hint?: string;
};

// AdapterProfile drives the test drawer per-merchant: which endpoints to
// expose, what auth header to display, default request bodies/queries, and
// whether a specific endpoint should route through the /templates connectivity
// probe (updates last_tested_at) instead of the generic proxy.
export type AdapterProfile = {
  label: string;
  // optionLabel is the short human-readable string shown in the create/update
  // dropdown. Together with ADAPTER_PROFILES keys it forms the single source
  // of truth for "which adapter_types the UI will let an operator pick"; no
  // hand-maintained <option> list can drift from the profile map.
  optionLabel: string;
  baseUrl: string;
  // authHeaderLine renders the header line shown in the request preview.
  // Bearer tokens and X-API-Key use different header shapes.
  authHeaderLine: (hint: string) => string;
  endpoints: EzEp[];
  defaultBodies: Record<string, string>;
  defaultQueries: Record<string, string>;
  // probePath (if set) routes that endpoint through /push-sites/:id/templates
  // instead of the generic /api proxy — used to update the site's
  // last_test_result badge. Absent means all endpoints go through the proxy.
  probePath?: string;
  // bindingSkeleton (if set) is the default field_mapping skeleton prefilled
  // into the binding row's field_mapping for adapters that have NO remote
  // templates to pick from (hasRemoteTemplates=false) — so the operator can
  // just fill in values instead of hand-typing JSON (doc/011). Values here are
  // placeholder defaults, empty value means the operator must supply it. Must
  // only contain keys in the adapter's allowed list. Absent → no auto-prefill.
  bindingSkeleton?: { key: string; value: string }[];
  // hasRemoteTemplates declares whether the merchant exposes a "list remote
  // templates" endpoint the operator picks from on /push-bindings. Ezlink does
  // (templates are separate objects referenced by id on CreateChannel);
  // Deepnix does not (channels are created directly with a full definition).
  // Binding form hides the 拉取/push_template_id controls when this is false.
  hasRemoteTemplates: boolean;
  // fieldMappingKeys declares the binding-row field_mapping whitelist (doc/011
  // + doc/041 第一批) for this adapter: the single source of truth for both the
  // binding-UI hints (required/optional keys + value hints) and the pre-save
  // validation (unknown-key warning, required-key error). It mirrors the
  // backend adapter's allowedFieldMappingKeys + required set. user_name was
  // previously here; it moved back to the push_site row's UserNameCiphertext
  // (alongside the password) because user_name is a SITE credential, not a
  // per-binding override. Absent → no whitelist/required constraints
  // (e.g. the custom-push no-op fallback).
  //
  // The required subset is derived via requiredBindingKeysFor() (see below) —
  // never duplicated here so /push-bindings, /push-sites badges and /distribute
  // warnings cannot drift from each other.
  fieldMappingKeys?: FieldMappingKeySpec[];
  // allowedAuthType restricts the auth_type dropdown on the /push-sites form
  // to one value when the adapter only supports one credential shape (e.g.
  // RockSolidAI only does basic). Absent → all of {token, basic} are shown
  // (custom-push / ezlink / deepnix fall under this).
  allowedAuthType?: "token" | "basic";
  // credentialFields declares what credential inputs the /push-sites form
  // should render for this adapter when auth_type=basic. Order matters: the
  // first entry is rendered as the primary "账号 user_name" input, the rest
  // (usually just "password") as the secret input. Absent → a single "auth"
  // input is rendered (token path).
  credentialFields?: Array<"user_name" | "password">;
  // Optional per-adapter guidance surfaced by the /push-sites create/edit
  // forms. Kept here so the form pulls its hints from the same source of
  // truth as the drawer.
  //
  // - authFieldHint: one-line note rendered under the credential input(s),
  //   explaining what they mean for this adapter (e.g. Deepnix = system
  //   token; RockSolidAI = user_name + password for the login handshake).
  //   Absent → no hint is shown.
  authFieldHint?: string;
  // showPlatformUserId surfaces the「对应系统用户Id」input on /push-sites for
  // newapi-derived merchants that accept a New-Api-User header.
  showPlatformUserId?: boolean;
  // bindingFields describes this adapter's field_mapping keys for the friendly
  // 「修改绑定参数」 modal on /push-bindings. Keys must be in the allowed list.
  bindingFields?: {
    key: string;
    label: string;
    placeholder?: string;
    type?: "text" | "number" | "decimal" | "select";
    required?: boolean;
    visibleByDefault?: boolean;
    options?: { value: string; label: string }[];
  }[];
  // siteOptionsFields declares SITE-level defaults written into
  // push_site.site_options (binding override wins; site fills the gap).
  siteOptionsFields?: {
    key: string;
    label: string;
    hint?: string;
    options: { value: string; label: string }[];
  }[];
};

const EZLINK_BASE_URL = "https://suply.ezlinkai.com/api/open/v1";
const DEEPNIX_BASE_URL = "https://open.deepnix.ai";
const ROCKSOILDAI_BASE_URL = "https://163.192.15.29:9443";
const FLYPIG_BASE_URL = "https://supplier.flypig.ai";

// 各 POST 端点的示例请求体(可编辑),GET 端点无 Body。
const EZLINK_DEFAULT_BODIES: Record<string, string> = {
  "/channels": JSON.stringify({ templateId: 9, inputs: { key: "sk-ant-xxx" }, siteIds: [18] }, null, 2),
  "/channels/batch": JSON.stringify(
    { templateId: 5, siteIds: [18], keySelectionMode: 1, rows: [{ key: "sk-xxx" }] },
    null,
    2
  ),
  "/rotation-pools": JSON.stringify({ templateId: 9, keys: "sk-a\nsk-b\nsk-c" }, null, 2),
};

// 各带查询参数端点的示例 query 对象(可编辑)。
const EZLINK_DEFAULT_QUERIES: Record<string, string> = {
  "/channel/search": JSON.stringify({ page: "1", pageSize: "20", channelId: "", startDate: "", endDate: "" }, null, 2),
  "/usage/summary": JSON.stringify({ startDate: "", endDate: "" }, null, 2),
};

const EZLINK_PROFILE: AdapterProfile = {
  label: "custom-ezlinkai-com · Ezlink Open API",
  optionLabel: "custom-ezlinkai-com(Ezlink)",
  baseUrl: EZLINK_BASE_URL,
  authHeaderLine: (hint) => `X-API-Key: ${hint} (redacted)`,
  probePath: "/templates",
  hasRemoteTemplates: true,
  authFieldHint: "Ezlink 系统 API Key(单串 token,一般以 sk- 开头)。填 token 明文,后端加密落库。",
  // 白名单映射 backend/internal/pushproto/ezlink/adapter.go:50 — 仅保留控制台侧
  // 站点钉扎 key:remote_site_id / tag_id。模板行输入键故意开放(每个模板自声明
  // inputs),不在白名单拦截内。ezlink 无必填 key(CreateChannel 成功与否不看
  // field_mapping,看模板 + siteIds)。示例值仅作文案提示,不预填。
  fieldMappingKeys: [
    {
      key: "remote_site_id",
      label: "远端站点 ID",
      type: "int",
      meaning: "把分发钉扎到单个远端站点 id,忽略模板绑定站点(建成后发送到该站点)",
      useCase: "该绑定只想固定发到某个特定下游站点时填;留空则按模板绑定站点分发",
      hint: "留空则用模板绑定站点",
    },
    {
      key: "tag_id",
      label: "标签 ID",
      type: "int",
      meaning: "按标签筛选取分发目标站点",
      useCase: "需要按标签批量筛站时填;留空则按模板绑定站点分发",
    },
  ],
  endpoints: [
    { idx: 0, m: "GET", path: "/templates", hasBody: false, hasQuery: false, note: "连通性 + 模板列表" },
    { idx: 1, m: "POST", path: "/channels", hasBody: true, hasQuery: false, note: "单条创建渠道", mutating: true },
    { idx: 2, m: "POST", path: "/channels/batch", hasBody: true, hasQuery: false, note: "批量创建渠道(真实创建)", mutating: true },
    { idx: 3, m: "GET", path: "/templates/key-rotation", hasBody: false, hasQuery: false, note: "支持超刷(Key 轮换)的模板" },
    { idx: 4, m: "GET", path: "/rotation-pools", hasBody: false, hasQuery: false, note: "超刷池概要" },
    { idx: 5, m: "GET", path: "/rotation-pools/10", hasBody: false, hasQuery: false, note: "超刷池详情(路径里的 id 可改)" },
    { idx: 6, m: "POST", path: "/rotation-pools", hasBody: true, hasQuery: false, note: "新建超刷池", mutating: true },
    { idx: 7, m: "GET", path: "/channel/search", hasBody: false, hasQuery: true, note: "逐渠道用量查询(page/pageSize/channelId/startDate/endDate)" },
    { idx: 8, m: "GET", path: "/usage/summary", hasBody: false, hasQuery: true, note: "账单用量汇总" },
  ],
  defaultBodies: EZLINK_DEFAULT_BODIES,
  defaultQueries: EZLINK_DEFAULT_QUERIES,
};

// Deepnix Body 示例:POST 用 new-api 包裹格式(顶层 mode/multi_key_mode + channel{}),
// PUT 用扁平格式(id 顶层)。示例中的 <UPSTREAM_KEY> 由运营在发送前替换,不落库。
const DEEPNIX_CREATE_BODY = JSON.stringify(
  {
    mode: "single",
    multi_key_mode: "random",
    channel: {
      name: "supplier-openai",
      platform_channel_type: "openai",
      key: "<UPSTREAM_KEY>",
      base_url: "",
      models: "gpt-4o-mini",
      group: "openai",
      priority: 6,
    },
  },
  null,
  2
);
// PUT is rendered via the drawer's method-swap; DEEPNIX_UPDATE_BODY is kept as
// a reference default that the operator can paste in when switching to PUT.
// It isn't wired into defaultBodies because the drawer keys defaults by path,
// not method; both POST /api/channel/ and PUT /api/channel/ share the same
// path bucket.
const _DEEPNIX_UPDATE_BODY = JSON.stringify(
  {
    id: 123,
    name: "supplier-openai",
    platform_channel_type: "openai",
    base_url: "",
    models: "gpt-4o-mini,gpt-4.1-mini",
    group: "openai",
    priority: 6,
  },
  null,
  2
);
void _DEEPNIX_UPDATE_BODY;

const DEEPNIX_DEFAULT_BODIES: Record<string, string> = {
  "/api/channel/": DEEPNIX_CREATE_BODY, // 默认展示 POST 语义;PUT 需运营手动改成扁平 body
};
const DEEPNIX_DEFAULT_QUERIES: Record<string, string> = {
  "/api/channel/": JSON.stringify({ p: "1", page_size: "10" }, null, 2),
  "/api/channel/search": JSON.stringify({ keyword: "", p: "1", page_size: "10" }, null, 2),
  "/api/channel/used-quota": JSON.stringify({ p: "1", page_size: "10" }, null, 2),
};

const DEEPNIX_PROFILE: AdapterProfile = {
  label: "custom-deepnix-ai · Deepnix Open API",
  optionLabel: "custom-deepnix-ai(Deepnix)",
  baseUrl: DEEPNIX_BASE_URL,
  authHeaderLine: (hint) => `Authorization: Bearer ${hint} (redacted)`,
  // Deepnix 无 template 概念;所有端点均经通用 proxy 打上游,
  // 保持"面板展示的请求 == 实际发送的请求"。last_tested_at 更新走探针分支,
  // 后端 probeTemplates 已支持 deepnix,预留给未来的独立测试按钮。
  hasRemoteTemplates: false,
  authFieldHint: "Deepnix system access token(Bearer 头单串 token)。填 token 明文,后端加密落库。",
  // 白名单映射 backend/internal/pushproto/deepnix/adapter.go allowedFieldMappingKeys;
  // platform_channel_type 枚举映射 backend/internal/pushproto/deepnix/types.go:27。
  fieldMappingKeys: [
    {
      key: "platform_channel_type",
      label: "平台类型",
      required: true,
      type: "enum",
      enum: [
        "openai", "anthropic_claude", "aws_claude", "aws_claude_new",
        "anthropic_param_override", "claude_platform", "vertex_ai_claude",
        "azure_claude", "meta_spark", "azure_openai", "google_gemini",
        "vertex_ai", "xai",
      ],
      meaning: "渠道对接的上游协议类型,决定 Deepnix 用哪个上游适配器",
      useCase: "每个渠道必填,默认 openai;枚举外的值后端直接拒绝创建",
      hint: "上游协议类型",
    },
    {
      key: "name",
      label: "渠道名",
      meaning: "渠道在 Deepnix 侧的名称",
      useCase: "留空自动用上游 Key 名称作为渠道名",
      hint: "留空则用上游 Key 名称",
    },
    {
      key: "mode",
      label: "模式",
      type: "enum",
      enum: ["single", "multi"],
      meaning: "Key 列表使用方式:single=单 key,multi=按 Key 池多 key",
      useCase: "给一个绑定配多把上游 Key 做负载均衡时用 multi;一把 Key 用 single",
      hint: "single=单 key,multi=多 key",
    },
    {
      key: "multi_key_mode",
      label: "多 key 策略",
      type: "enum",
      enum: ["random", "polling"],
      meaning: "multi 模式取 Key 策略:random=随机,polling=轮询",
      useCase: "仅在 mode=multi 时有效;single 下无效",
      hint: "multi 模式下生效",
    },
    {
      key: "group",
      label: "分组",
      meaning: "渠道分组名,Deepnix 侧用于路由/选路",
      useCase: "同组多通道时填相同 group 便于分组管理;留空用 Deepnix 默认",
      hint: "渠道分组名",
    },
    {
      key: "upstream_base_url",
      label: "上游地址",
      meaning: "覆盖上游供应商默认地址(如 Azure OpenAI 部署 URL)",
      useCase: "上游为自建网关/特殊部署时填;否则留空用默认",
      hint: "留空用系统默认",
    },
    {
      key: "priority",
      label: "优先级",
      type: "int",
      meaning: "同分组内渠道的优先级,参与 Deepnix 选路排序",
      useCase: "同分组多条渠道需要显式分位次时设置;留空为 0",
      hint: "值越大越优先",
    },
    {
      key: "weight",
      label: "权重",
      type: "int",
      meaning: "同一优先级档内的分发权重,偏向同档渠道被选中的概率",
      useCase: "同分组同 priority 的渠道需不均匀分配流量时设置",
    },
    {
      key: "auto_ban",
      label: "自动封禁",
      type: "int",
      meaning: "上游持续失败时是否自动禁用该渠道(0=关,1=开)",
      useCase: "想自动摘除故障渠道时设 1;需人工运维时设 0",
      hint: "0=关,1=开",
    },
  ],
  bindingSkeleton: [
    { key: "platform_channel_type", value: "openai" },
    { key: "mode", value: "single" },
    { key: "multi_key_mode", value: "random" },
    { key: "group", value: "default" },
    { key: "priority", value: "6" },
  ],
  endpoints: [
    { idx: 0, m: "GET", path: "/api/channel/", hasBody: false, hasQuery: true, note: "渠道列表(p/page_size)" },
    { idx: 1, m: "GET", path: "/api/channel/search", hasBody: false, hasQuery: true, note: "关键字搜索(keyword/p/page_size)" },
    { idx: 2, m: "GET", path: "/api/channel/used-quota", hasBody: false, hasQuery: true, note: "渠道用量汇总(p/page_size)" },
    {
      idx: 3,
      m: "GET",
      path: "/api/channel/{id}",
      hasBody: false,
      hasQuery: false,
      note: "渠道详情(先从渠道列表或创建响应 data.id 获取真实 ID,再替换路径里的 {id})",
    },
    { idx: 4, m: "POST", path: "/api/channel/", hasBody: true, hasQuery: false, note: "创建渠道(真实创建 · new-api 包裹 body)", mutating: true },
    { idx: 5, m: "PUT", path: "/api/channel/", hasBody: true, hasQuery: false, note: "更新渠道(扁平 body · id 顶层)", mutating: true },
  ],
  defaultBodies: DEEPNIX_DEFAULT_BODIES,
  defaultQueries: DEEPNIX_DEFAULT_QUERIES,
};

// RockSolidAI(账号密码握手 + 12h Bearer)。账号 user_name + 密码 password
// 都是站点凭证(auth_type=basic,加密落 push_site.user_name_ciphertext 与
// auth_ciphertext);绑定行 field_mapping 只承载 channel_group_id / mode /
// name / base_url / models / priority 等非凭据参数。业务请求带的是握手
// 拿到的短期 Bearer(后端 Client 内部维护 token 缓存,前端无需感知)。
// self-signed 证书由后端 pinned CA 校验,通用 /api/proxy 未接入此适配器 ——
// drawer 里只暴露一个「获取」端点走 /templates 探针(后端映射到
// POST /api/channel/list),用于「行测试 / 先测后存」。
const ROCKSOILDAI_LIST_BODY = JSON.stringify({ page: 1, pageSize: 10 }, null, 2);

const ROCKSOILDAI_DEFAULT_BODIES: Record<string, string> = {
  "/api/channel/list": ROCKSOILDAI_LIST_BODY,
  "/api/channel/create": JSON.stringify({
    channelGroupId: 2,
    name: "supplier-channel",
    key: "REPLACE_WITH_UPSTREAM_KEY",
    models: "gpt-4o-mini",
    priority: 6,
  }, null, 2),
  "/api/channel/usage": JSON.stringify({ channelId: 1 }, null, 2),
};

const ROCKSOILDAI_PROFILE: AdapterProfile = {
  label: "custom-rocksoild-ai · RockSolidAI 商家渠道 API",
  optionLabel: "custom-rocksoild-ai(RockSolidAI)",
  baseUrl: ROCKSOILDAI_BASE_URL,
  authHeaderLine: (hint) =>
    `Authorization: Bearer <handshake-token>  (password hint: ${hint})`,
  hasRemoteTemplates: false,
  // 白名单映射 backend/internal/pushproto/rocksoildai/adapter.go allowedFieldMappingKeys;
  // channel_group_id 取整合法集见 backend/internal/pushproto/rocksoildai/types.go:27(1..13,
  // 商家接口 POST /api/web/channel-group/list)。
  fieldMappingKeys: [
    {
      key: "channel_group_id",
      label: "渠道分组 ID",
      required: true,
      type: "int",
      meaning: "渠道所属分组/类型族 id(闭集 1..13,商家 /api/web/channel-group/list)",
      useCase: "创建渠道必须归属一个分组;分组 2(AWS Bedrock-新版)等可能需要 base_url",
      hint: "1–13,从商家后台渠道分组列表获取",
    },
    {
      key: "name",
      label: "渠道名",
      meaning: "渠道显示名(RockSolidAI 服务端会自动追加随机后缀)",
      useCase: "留空自动用上游 Key 名称;后端兜底要求 name 非空",
      hint: "留空则用上游 Key 名称",
    },
    {
      key: "base_url",
      label: "上游地址",
      meaning: "覆盖上游默认地址",
      useCase: "分组 2(AWS Bedrock-新版)等要求指定上游时必填",
    },
    {
      key: "models",
      label: "模型列表",
      meaning: "渠道开放的模型白名单",
      useCase: "想限制渠道可用模型时填;留空由商家端决定",
      hint: "逗号分隔",
    },
    {
      key: "mode",
      label: "模式",
      type: "int",
      meaning: "渠道创建模式,仅支持 0=单 key(商家 UI 的 1 属于 channel/batch-add,创建不可用)",
      useCase: "保持默认 0 即可",
      hint: "0=默认",
    },
    {
      key: "priority",
      label: "优先级",
      type: "int",
      meaning: "渠道排序优先级(0–1000,nil=商家默认)",
      useCase: "同分组渠道想显式区分位次时设置;留空用商家默认",
    },
  ],
  bindingSkeleton: [
    { key: "channel_group_id", value: "2" },
    { key: "name", value: "supplier-channel" },
    { key: "mode", value: "0" },
    { key: "priority", value: "6" },
  ],
  // RockSolidAI 只支持 basic:站点凭证 = user_name + password 两栏位。
  allowedAuthType: "basic",
  credentialFields: ["user_name", "password"],
  authFieldHint:
    "填账号「user_name + password」两个明文。user_name 加密落 user_name_ciphertext、password 加密落 auth_ciphertext;后端握手时 POST /api/auth/login 用两者换 12h Bearer。",
  endpoints: [
    {
      idx: 0,
      m: "POST",
      path: "/api/channel/list",
      hasBody: true,
      hasQuery: false,
      note: "获取渠道列表(后端处理登录握手与 TLS pinning)",
    },
    { idx: 1, m: "POST", path: "/api/channel/create", hasBody: true, hasQuery: false, note: "创建渠道(真实写操作)", mutating: true },
    { idx: 2, m: "POST", path: "/api/channel/usage", hasBody: true, hasQuery: false, note: "查询单渠道用量" },
  ],
  defaultBodies: ROCKSOILDAI_DEFAULT_BODIES,
  defaultQueries: {},
};

const FLYPIG_DEFAULT_BODIES: Record<string, string> = {
  "/api/v1/keys/upload": JSON.stringify(
    {
      key_type_code: "REPLACE_WITH_KEY_TYPE_CODE",
      external_batch_id: "REPLACE_WITH_EXTERNAL_BATCH_ID",
      keys: ["REPLACE_WITH_UPSTREAM_KEY"],
    },
    null,
    2
  ),
};

const FLYPIG_DEFAULT_QUERIES: Record<string, string> = {
  "/api/v1/keys": JSON.stringify(
    {
      q: "",
      status: "",
      key_type_id: "0",
      liveness_filter: "",
      usage_filter: "",
      sort: "",
      limit: "50",
      offset: "0",
    },
    null,
    2
  ),
  "/api/v1/uploads": JSON.stringify({ limit: "50", offset: "0" }, null, 2),
};

const FLYPIG_PROFILE: AdapterProfile = {
  label: "custom-flypig · Flypig Supplier API",
  optionLabel: "custom-flypig(Flypig)",
  baseUrl: FLYPIG_BASE_URL,
  authHeaderLine: (hint) => `Authorization: Bearer ${hint} (redacted)`,
  probePath: "/api/v1/me",
  hasRemoteTemplates: false,
  // 白名单映射 backend/internal/pushproto/flypig/adapter.go:20(仅 key_type_code)。
  // 取值来源 Flypig 平台 /catalog;v1 不自动拉取(见 doc/019 §2.2),仅文案提示。
  fieldMappingKeys: [
    {
      key: "key_type_code",
      label: "密钥类型码",
      required: true,
      type: "string",
      meaning: "上传 Key 时声明的密钥类型码,决定该 Key 在 Flypig 平台归入哪类(如 openai.standard)",
      useCase: "每个绑定必填;取值从 Flypig 平台 /catalog 获取(v1 不自动拉取,doc/019 §2.2)",
      hint: "从 Flypig 平台 /catalog 获取(如 openai.standard)",
    },
  ],
  bindingSkeleton: [{ key: "key_type_code", value: "openai.standard" }],
  allowedAuthType: "token",
  authFieldHint: "Flypig Supplier Token(Bearer 单串 token)。填 Token 明文,后端加密落库。",
  endpoints: [
    { idx: 0, m: "GET", path: "/api/v1/me", hasBody: false, hasQuery: false, note: "供应商身份 + Token 连通性" },
    { idx: 1, m: "GET", path: "/api/v1/catalog", hasBody: false, hasQuery: false, note: "可上传密钥类型(key_type_code)" },
    { idx: 2, m: "POST", path: "/api/v1/keys/upload", hasBody: true, hasQuery: false, note: "批量上传 Key(真实写操作)", mutating: true },
    { idx: 3, m: "GET", path: "/api/v1/keys", hasBody: false, hasQuery: true, note: "Key 台账筛选与分页" },
    { idx: 4, m: "GET", path: "/api/v1/keys/{key_id}", hasBody: false, hasQuery: false, note: "单个 Key 汇总详情(需替换真实 key_id)" },
    { idx: 5, m: "POST", path: "/api/v1/keys/{key_id}/sync", hasBody: false, hasQuery: false, note: "触发单 Key 真实用量同步(真实写操作)", mutating: true },
    { idx: 6, m: "GET", path: "/api/v1/uploads", hasBody: false, hasQuery: true, note: "上传批次列表" },
    { idx: 7, m: "GET", path: "/api/v1/uploads/{batch_id}", hasBody: false, hasQuery: false, note: "上传批次明细(需替换真实 batch_id)" },
    { idx: 8, m: "GET", path: "/api/v1/usage/summary", hasBody: false, hasQuery: false, note: "真实用量汇总" },
  ],
  defaultBodies: FLYPIG_DEFAULT_BODIES,
  defaultQueries: FLYPIG_DEFAULT_QUERIES,
};

// aiabc(tool.aiabc.cfd,页面标题「AWS Channel 提交工具」)是下游自研推送站。
// 认证是「只有访问密码」的表单登录换 session cookie(无用户名、无 Bearer);
// 控制台 auth_type=basic,但 user_name 只是满足约束的占位、适配器不使用它。
// 目标站点(必填)= api_config_id,候选来自 /api/keyupload-api-configs,复用
// 「远程模板」机制:拉取→选择→存 push_template_id→成为 deploy 的
// api_config_ids[0]。渠道名由后端生成,响应无数字 id(靠名字回查)。
const AIABC_BASE_URL = "https://tool.aiabc.cfd";

const AIABC_DEFAULT_BODIES: Record<string, string> = {
  "/api/keyupload/deploy": JSON.stringify(
    {
      api_config_ids: [1],
      channel_type: 1,
      models: "gpt-5.5,gpt-5.5-pro",
      socks5: "",
      socks5_id: "",
      base_url: "",
      keys_text: "REPLACE_WITH_UPSTREAM_KEY",
      aggregate: false,
    },
    null,
    2
  ),
};

const AIABC_DEFAULT_QUERIES: Record<string, string> = {
  "/api/keyupload/channels": JSON.stringify({ api_config_id: "1", page: "1", page_size: "50" }, null, 2),
  "/api/keyupload/quota-query": JSON.stringify({ api_config_id: "1", keyword: "", max_count: "50" }, null, 2),
};

const AIABC_PROFILE: AdapterProfile = {
  label: "custom-aiabc-cfd · aiabc 推送站",
  optionLabel: "custom-aiabc-cfd(aiabc)",
  baseUrl: AIABC_BASE_URL,
  authHeaderLine: (hint) => `Cookie: session=<login>  (访问密码 hint: ${hint})`,
  hasRemoteTemplates: true,
  allowedAuthType: "basic",
  credentialFields: ["user_name", "password"],
  authFieldHint:
    "该站只有「访问密码」:填任意占位 user_name(仅满足 basic 约束)+ 访问密码。适配器忽略 user_name,仅用密码 POST /login 换 session cookie;password 加密落 auth_ciphertext。",
  // 白名单映射 backend/internal/pushproto/aiabc/adapter.go:allowedFieldMappingKeys。
  // 均为可选:空 field_mapping 也可保存(凭据可先测);必填的只是「目标站点」选择
  // (push_template_id,非 field_mapping key)。channel_type 缺省时取目标站点自身的
  // channel_type;aggregate 由 BizConsole 单 Key 分发恒 false。
  fieldMappingKeys: [
    {
      key: "channel_type",
      label: "渠道类型码",
      type: "enum",
      enum: ["1", "3", "14", "24", "25", "41", "48"],
      meaning: "deploy 声明的 newapi channel_type,决定渠道按哪种上游协议接入",
      useCase: "留空 → 取所选目标站点(api_config_id)自身的 channel_type;需要覆盖时填",
      hint: "1/3/14/24/25/41/48 之一",
    },
    {
      key: "models",
      label: "模型白名单",
      type: "string",
      meaning: "渠道对外声明的模型列表(CSV),留空则用后端默认",
      useCase: "留空 → 继承分发请求级 models;需要按绑定覆盖时填",
      hint: "如 gpt-5.5,gpt-5.5-pro",
    },
    {
      key: "base_url",
      label: "上游 Base URL",
      type: "string",
      meaning: "覆盖渠道上游地址,留空则用上游 Key 自身的 base_url",
      useCase: "仅当需要指向非默认上游时填",
      hint: "如 https://api.example.com",
    },
    {
      key: "socks5",
      label: "Socks5 代理",
      type: "string",
      meaning: "该下游渠道出网用的 socks5 代理地址",
      useCase: "需要经代理出网时填",
      hint: "socks5://host:port",
    },
    {
      key: "socks5_id",
      label: "Socks5 代理 ID",
      type: "string",
      meaning: "socks5 代理在 aiabc 侧的登记 id(与该站代理池配套)",
      useCase: "使用 aiabc 代理池登记项时填",
      hint: "aiabc 侧代理 id",
    },
    {
      key: "aggregate",
      label: "聚合渠道",
      type: "enum",
      enum: ["true", "false"],
      meaning: "是否把多个 Key 合并为一个渠道",
      useCase: "BizConsole 每个任务推一个 Key,通常保持 false",
      hint: "true / false(默认 false)",
    },
    {
      key: "keyword",
      label: "渠道名关键字",
      type: "string",
      meaning: "quota-query 按渠道名过滤用的关键字,留空则取登录账户默认关键字",
      useCase: "仅当账户存在多个关键字、需要按绑定指定时填",
      hint: "留空取账户默认",
    },
  ],
  endpoints: [
    { idx: 0, m: "GET", path: "/api/keyupload/upload-quota", hasBody: false, hasQuery: false, note: "连通性 + 每分钟上传额度(低成本探针)" },
    { idx: 1, m: "GET", path: "/api/keyupload-api-configs", hasBody: false, hasQuery: false, note: "目标站点候选(= 绑定「拉取」的来源)" },
    { idx: 2, m: "GET", path: "/api/keyupload/channels", hasBody: false, hasQuery: true, note: "渠道列表(api_config_id/page/page_size)" },
    { idx: 3, m: "GET", path: "/api/keyupload/quota-query", hasBody: false, hasQuery: true, note: "渠道用量(api_config_id/keyword/max_count)" },
    { idx: 4, m: "POST", path: "/api/keyupload/deploy", hasBody: true, hasQuery: false, note: "创建渠道(真实写操作 · keys_text 为上游明文,仅本次瞬时使用)" },
  ],
  defaultBodies: AIABC_DEFAULT_BODIES,
  defaultQueries: AIABC_DEFAULT_QUERIES,
};

// WELL_KNOWN_FIELD_KEYS are the field_mapping overrides every push adapter
// accepts uniformly (backend fieldmap.WellKnownKeys) and that the dispatcher
// applies to the push snapshot in ONE place (pushadapter
// ApplyFieldMappingOverrides): models / model_mapping. They are unioned into
// every profile's fieldMappingKeys below so the binding form exposes them
// everywhere, instead of each merchant re-declaring the same two keys.
export const WELL_KNOWN_FIELD_KEYS: FieldMappingKeySpec[] = [
  {
    key: "models",
    label: "模型列表",
    type: "string",
    meaning: "覆盖本次分发的模型白名单(CSV),写到快照 snap.Models",
    useCase: "按绑定覆盖上游 Key 的模型集;留空则用上游 Key / 分发请求级的模型",
    hint: "逗号分隔,如 gpt-4o-mini,gpt-4.1-mini",
  },
  {
    key: "model_mapping",
    label: "模型映射",
    type: "string",
    meaning: "模型重定向映射(JSON 对象字符串),写到快照 snap.ModelMapping",
    useCase: "需要把请求模型名映射到上游模型名时填;留空用上游 Key 的映射",
    hint: 'JSON 对象,如 {"gpt-4o":"gpt-4o-2024"}',
  },
];

// withWellKnownKeys returns a copy of the profile with WELL_KNOWN_FIELD_KEYS
// appended to fieldMappingKeys, skipping any key the profile already declares
// (so a merchant's richer spec for e.g. "models" wins). Declared specs are
// never mutated.
function withWellKnownKeys(profile: AdapterProfile): AdapterProfile {
  const own = profile.fieldMappingKeys ?? [];
  const have = new Set(own.map((k) => k.key));
  const extra = WELL_KNOWN_FIELD_KEYS.filter((k) => !have.has(k.key));
  return { ...profile, fieldMappingKeys: [...own, ...extra] };
}


// custom-ximeizhi-silicon-ovhish 是 newapi 定制 fork 的 seller 推送站:
// Bearer 站点令牌直连,创建 POST /api/seller/channel/(响应无 id,靠渠道名
// 回查),用量取渠道累计 used_quota。无远程模板概念 → 绑定用 field_mapping。
const XIMEIZHI_BASE_URL = "https://silicon-ovhish.haoyi666.com";

const XIMEIZHI_DEFAULT_BODIES: Record<string, string> = {
  "/api/seller/channel/": JSON.stringify(
    {
      mode: "single",
      channel: {
        name: "my-channel",
        type: 1,
        base_url: null,
        key: "REPLACE_WITH_UPSTREAM_KEY",
        models: "gpt-4o,gpt-4o-mini",
        group: "openai",
        model_mapping: "",
        status: 1,
        remark: "",
      },
    },
    null,
    2
  ),
};

const XIMEIZHI_DEFAULT_QUERIES: Record<string, string> = {
  "/api/seller/channel/": JSON.stringify({ page: "1", page_size: "20" }, null, 2),
  "/api/seller/channel/runtime_metrics": JSON.stringify({ channel_ids: "1", metric_window: "1m" }, null, 2),
};

const XIMEIZHI_PROFILE: AdapterProfile = {
  label: "custom-ximeizhi-silicon-ovhish · 西梅汁 seller(newapi)",
  optionLabel: "custom-ximeizhi-silicon-ovhish(西梅汁)",
  baseUrl: XIMEIZHI_BASE_URL,
  authHeaderLine: (hint) => `Authorization: Bearer ${hint} (redacted)`,
  hasRemoteTemplates: false,
  bindingSkeleton: [
    { key: "group", value: "openai" },
    { key: "channel_type", value: "1" },
    { key: "name", value: "" },
    { key: "models", value: "" },
  ],
  bindingFields: [
    { key: "group", label: "分组 group", placeholder: "openai", required: true },
    { key: "channel_type", label: "渠道类型 channel_type", type: "number", placeholder: "1" },
    { key: "models", label: "模型 models", placeholder: "留空 = 用主模板模型" },
    { key: "model_mapping", label: "模型映射 model_mapping", placeholder: "留空 = 不映射" },
    { key: "name", label: "渠道名 name", placeholder: "留空 = 用上游 Key 名" },
    { key: "base_url", label: "base_url", placeholder: "留空 = 用上游 base_url" },
    { key: "priority", label: "优先级 priority", type: "number", placeholder: "留空 = 站点默认" },
    { key: "proxy", label: "出站代理 proxy", placeholder: "留空 = 不设" },
    { key: "remark", label: "备注 remark", placeholder: "留空 = 无" },
  ],
  allowedAuthType: "token",
  showPlatformUserId: true,
  authFieldHint:
    "站点访问令牌(该站「访问令牌」页创建的一串随机值)。填明文,后端加密落 auth_ciphertext;适配器用 Authorization: Bearer <token> 调 /api/seller/channel/*。",
  endpoints: [
    { idx: 0, m: "GET", path: "/api/seller/channel/", hasBody: false, hasQuery: true, note: "渠道列表(page/page_size)· 兼作连通性探针" },
    { idx: 1, m: "GET", path: "/api/seller/channel/meta", hasBody: false, hasQuery: false, note: "分组 / 模型候选" },
    { idx: 2, m: "GET", path: "/api/seller/channel/runtime_metrics", hasBody: false, hasQuery: true, note: "运行时速率窗口(channel_ids/metric_window)" },
    { idx: 3, m: "GET", path: "/api/seller/channel/{id}", hasBody: false, hasQuery: false, note: "单渠道详情(路径里的 id 可改)" },
    { idx: 4, m: "POST", path: "/api/seller/channel/", hasBody: true, hasQuery: false, note: "创建渠道(真实写操作 · key 为上游明文,仅本次瞬时使用)", mutating: true },
  ],
  defaultBodies: XIMEIZHI_DEFAULT_BODIES,
  defaultQueries: XIMEIZHI_DEFAULT_QUERIES,
};

// custom-rocketcloudai(key.rocketcloud.ai)是 newapi 多租户 BFF:
// 账号 + 密码换 rc_sess cookie,账户自带渠道模板。upload_mode=aggregate
// 让一批 key 聚合成一个下游渠道(mode=multi_to_single)。
const ROCKETCLOUD_BASE_URL = "https://key.rocketcloud.ai";

const ROCKETCLOUD_DEFAULT_QUERIES: Record<string, string> = {
  "/api/platforms/{id}/channels": JSON.stringify({ p: "0", page_size: "50", pageSize: "50" }, null, 2),
};

const ROCKETCLOUD_DEFAULT_BODIES: Record<string, string> = {
  "/api/platforms/{id}/channels": JSON.stringify(
    {
      mode: "single",
      channel: {
        name: "supplier-openai",
        type: 1,
        key: "REPLACE_WITH_UPSTREAM_KEY",
        base_url: "",
        models: "gpt-5.6-sol,gpt-6-astra",
        group: "openai",
        status: 1,
      },
    },
    null,
    2
  ),
  "/api/history/newapi": JSON.stringify(
    {
      platformId: 17,
      templateName: "openai模板",
      successCount: 1,
      keys: [{ name: "supplier-openai", key: "REPLACE_WITH_UPSTREAM_KEY", status: "ok" }],
    },
    null,
    2
  ),
};

const ROCKETCLOUD_PROFILE: AdapterProfile = {
  label: "custom-rocketcloudai · rocketcloud BFF 控制台",
  optionLabel: "custom-rocketcloudai(rocketcloud)",
  baseUrl: ROCKETCLOUD_BASE_URL,
  authHeaderLine: (hint) => `Cookie: rc_sess=<login>  (password hint: ${hint})`,
  probePath: "/api/templates",
  hasRemoteTemplates: true,
  bindingFields: [
    { key: "models", label: "模型 models", placeholder: "留空 = 用主模板模型" },
    {
      key: "upload_mode",
      label: "上传方式 upload_mode",
      type: "select",
      options: [
        { value: "", label: "继承站点默认" },
        { value: "single", label: "single(逐 key 建渠道)" },
        { value: "aggregate", label: "aggregate(整批聚合 1 渠道)" },
      ],
    },
    {
      key: "multi_key_mode",
      label: "聚合策略 multi_key_mode",
      type: "select",
      options: [
        { value: "", label: "继承站点默认" },
        { value: "random", label: "random" },
        { value: "polling", label: "polling" },
      ],
    },
    { key: "channel_name_suffix", label: "渠道名后缀 channel_name_suffix", placeholder: "留空 = 无;≤32 字符,不能含括号" },
  ],
  siteOptionsFields: [
    {
      key: "upload_mode",
      label: "默认上传方式 upload_mode",
      hint: "整站默认:aggregate 时该站的整批 key 聚合成 1 个下游渠道;绑定行可单独覆盖。",
      options: [
        { value: "", label: "未设置(逐 key 建渠道)" },
        { value: "single", label: "single(逐 key 建渠道)" },
        { value: "aggregate", label: "aggregate(整批聚合 1 渠道)" },
      ],
    },
    {
      key: "multi_key_mode",
      label: "默认聚合策略 multi_key_mode",
      hint: "聚合渠道内多把 key 的轮换策略;绑定行可单独覆盖。",
      options: [
        { value: "", label: "未设置(random)" },
        { value: "random", label: "random" },
        { value: "polling", label: "polling" },
      ],
    },
  ],
  allowedAuthType: "basic",
  credentialFields: ["user_name", "password"],
  authFieldHint:
    "填站点「账号 user_name + 密码」两个明文。user_name 加密落 user_name_ciphertext、password 加密落 auth_ciphertext;适配器每次调用 POST /api/login 换 rc_sess cookie 再访问 /api/platforms/*。",
  endpoints: [
    { idx: 0, m: "GET", path: "/api/templates", hasBody: false, hasQuery: false, note: "连通性 + 模板列表(= 绑定「拉取」的来源)" },
    { idx: 1, m: "GET", path: "/api/me", hasBody: false, hasQuery: false, note: "账号信息(companyAbbr/companyName,渠道名渲染用)" },
    { idx: 2, m: "GET", path: "/api/platforms", hasBody: false, hasQuery: false, note: "平台列表(取真实 {id} 填到下面路径)" },
    { idx: 3, m: "GET", path: "/api/platforms/{id}/models", hasBody: false, hasQuery: false, note: "平台模型候选(把 {id} 替换为真实平台 id)" },
    { idx: 4, m: "GET", path: "/api/platforms/{id}/channels", hasBody: false, hasQuery: true, note: "渠道列表(p/page_size,需替换 {id};后端自动带 X-Platform-Id 头)" },
    { idx: 5, m: "POST", path: "/api/platforms/{id}/channels", hasBody: true, hasQuery: false, note: "创建渠道(真实写操作 · 需替换 {id};key 为上游明文,仅本次瞬时使用)", mutating: true },
    { idx: 6, m: "POST", path: "/api/history/newapi", hasBody: true, hasQuery: false, note: "对账记录(真实写操作 · 商家页面每次创建成功后写入)", mutating: true },
  ],
  defaultBodies: ROCKETCLOUD_DEFAULT_BODIES,
  defaultQueries: ROCKETCLOUD_DEFAULT_QUERIES,
};

// custom-ximeizi-lk999ai(token.lk999.ai)是「开放 API」单 Bearer 密钥站点:
// 全 POST、无远程模板。上号 = POST /accounts;用量 USD 来自 /bill/query。
const XIMEIZHI_LK999AI_BASE_URL = "https://token.lk999.ai";

const XIMEIZHI_LK999AI_DEFAULT_BODIES: Record<string, string> = {
  "/api/v1/openapi/accounts": JSON.stringify(
    {
      provider: "openai",
      model: "gpt-4o,gpt-4o-mini",
      api_key: "REPLACE_WITH_UPSTREAM_KEY",
      tpm: 500000,
      rpm: 500,
      discount: 88.5,
    },
    null,
    2
  ),
  "/api/v1/openapi/metrics/query": JSON.stringify(
    { account_ids: [0], begin_time: "", end_time: "", aggregate: false },
    null,
    2
  ),
  "/api/v1/openapi/metrics/query-keys": JSON.stringify(
    { api_keys: ["REPLACE_WITH_UPSTREAM_KEY"], begin_time: "", end_time: "", aggregate: false },
    null,
    2
  ),
  "/api/v1/openapi/bill/query": JSON.stringify(
    { account_ids: [0], start_ds: "2026-07-22", end_ds: "2026-10-19" },
    null,
    2
  ),
  "/api/v1/openapi/proxy-options": JSON.stringify(
    { provider: "openai" },
    null,
    2
  ),
};

const XIMEIZHI_LK999AI_PROFILE: AdapterProfile = {
  label: "custom-ximeizi-lk999ai · lk999 开放 API",
  optionLabel: "custom-ximeizi-lk999ai(lk999)",
  baseUrl: XIMEIZHI_LK999AI_BASE_URL,
  authHeaderLine: (hint) => `Authorization: Bearer ${hint} (redacted)`,
  hasRemoteTemplates: false,
  bindingSkeleton: [
    { key: "provider", value: "openai" },
    { key: "models", value: "" },
    { key: "tpm", value: "" },
    { key: "rpm", value: "" },
    { key: "discount", value: "" },
  ],
  bindingFields: [
    {
      key: "provider",
      label: "厂商 provider",
      placeholder: "openai / anthropic / google / aws_third / azure_openai / azure_claude",
      required: true,
    },
    { key: "models", label: "模型 models", placeholder: "逗号分隔;留空 = 用上游 Key 模型" },
    { key: "account_type", label: "账号类型 account_type", type: "number", placeholder: "0 普通 / 1 速刷号(仅 anthropic)" },
    { key: "tpm", label: "TPM(每分钟 token)", type: "number", placeholder: "普通号必填,如 500000" },
    { key: "rpm", label: "RPM(每分钟请求)", type: "number", placeholder: "普通号必填,如 500" },
    { key: "discount", label: "折扣 discount", type: "decimal", placeholder: "去掉百分号,>0 且 ≤100,最多 2 位小数,如 49.5" },
    { key: "region", label: "账号注册地 region", placeholder: "Google / AWS 必填" },
    { key: "proxy_type", label: "代理配置 proxy_type", placeholder: "从 /proxy-options 获取,如 bind_single;留空 = 平台默认" },
    { key: "proxy_region", label: "代理地区 proxy_region", placeholder: "从 /proxy-options 获取,如 美国;region_required=false 时留空" },
    { key: "url", label: "自定义地址 url", placeholder: "ali 可选" },
    { key: "adc_config", label: "ADC 配置 adc_config", placeholder: 'Google 服务账号 JSON / AWS arns JSON / Azure {"url":...}' },
    { key: "api_secret", label: "AWS 密钥 api_secret", placeholder: "aws_auth_type=aksk 时必填(敏感)" },
    { key: "aws_region", label: "AWS 区域 aws_region", placeholder: "AWS 必填" },
    { key: "aws_project_id", label: "AWS 项目 ID aws_project_id", placeholder: "ARN 方式必填" },
    { key: "aws_auth_type", label: "AWS 鉴权方式 aws_auth_type", placeholder: "留空/arn = ARN;aksk = AK/SK" },
    { key: "group_id", label: "分组 group_id", placeholder: "minimax 可选" },
    { key: "remark", label: "备注 remark", placeholder: "≤255 字符" },
  ],
  allowedAuthType: "token",
  authFieldHint:
    "lk999「开放 API 密钥」(msk_live_…)。填明文,后端加密落 auth_ciphertext;适配器用 Authorization: Bearer <key> 调 /api/v1/openapi/*。站点全部 POST,无删除/更新端点。",
  endpoints: [
    { idx: 0, m: "POST", path: "/api/v1/openapi/accounts", hasBody: true, hasQuery: false, note: "上号(真实写操作 · api_key 为上游明文,仅本次瞬时使用)", mutating: true },
    { idx: 1, m: "POST", path: "/api/v1/openapi/metrics/query", hasBody: true, hasQuery: false, note: "实时用量(account_ids/begin_time/end_time/aggregate;cost 恒为 0,非金额)" },
    { idx: 2, m: "POST", path: "/api/v1/openapi/metrics/query-keys", hasBody: true, hasQuery: false, note: "按 Key 查实时用量(兼作连通性探针;匹配不到的 key 进 unmatched)" },
    { idx: 3, m: "POST", path: "/api/v1/openapi/bill/query", hasBody: true, hasQuery: false, note: "离线账单(account_ids/start_ds/end_ds;list[].cost 为 USD,start_ds 不早于 90 天前)" },
    { idx: 4, m: "POST", path: "/api/v1/openapi/proxy-options", hasBody: true, hasQuery: false, note: "代理配置/地区查询(provider 必填,proxy_type 可选;上号前现查,平台缓存 30 秒)" },
  ],
  defaultBodies: XIMEIZHI_LK999AI_DEFAULT_BODIES,
  defaultQueries: {},
};

// 101aix Supplier OpenAPI:Supplier Token + 异步单 Key 批次。绑定时动态
// 拉取 OpenAI API Key 渠道并把渠道 id 保存为 push_template_id。
const AIXNET101_BASE_URL = "https://lingshu.101aix.net";
const AIXNET101_CHANNELS_PATH = "/api/v1/openapi/supplier/channels/";
const AIXNET101_BATCHES_PATH = "/api/v1/openapi/supplier/key-batches/";
const AIXNET101_RECONCILIATION_PATH = "/api/v1/openapi/supplier/channels/{channel_id}/reconciliation/items/";

const AIXNET101_DEFAULT_BODIES: Record<string, string> = {
  [AIXNET101_BATCHES_PATH]: JSON.stringify(
    {
      channel_id: "REPLACE_WITH_CHANNEL_ID",
      rows: [
        {
          official_credential: { api_key: "REPLACE_WITH_UPSTREAM_KEY" },
          client_ref: "console-row-1",
          account_level: "T1",
          quota_unlimited: true,
          consumption_time_hours: 168,
          access_test_model_mode: "inherit",
          supplier_remark: "",
        },
      ],
    },
    null,
    2
  ),
  [AIXNET101_RECONCILIATION_PATH]: JSON.stringify(
    {
      api_key: "REPLACE_WITH_UPSTREAM_KEY",
      batch_scope_key: "",
      page: 1,
      page_size: 100,
    },
    null,
    2
  ),
};

const AIXNET101_DEFAULT_QUERIES: Record<string, string> = {
  [AIXNET101_BATCHES_PATH]: JSON.stringify({ channel_id: "", page: "1", page_size: "100" }, null, 2),
  "/api/v1/openapi/supplier/key-batches/{batch_id}/items/": JSON.stringify({ page: "1", page_size: "100" }, null, 2),
  [AIXNET101_RECONCILIATION_PATH]: JSON.stringify({ batch_scope_key: "", page: "1", page_size: "100" }, null, 2),
};

const AIXNET101_PROFILE: AdapterProfile = {
  label: "custom-101aixnet · 101aix Supplier OpenAPI",
  optionLabel: "custom-101aixnet(101aix)",
  baseUrl: AIXNET101_BASE_URL,
  authHeaderLine: (hint) => `Authorization: Bearer ${hint} (redacted)`,
  hasRemoteTemplates: true,
  allowedAuthType: "token",
  authFieldHint:
    "101aix Supplier Token(单串 Bearer token)。填明文，后端加密落 auth_ciphertext；仅展示可用的 OpenAI API Key 渠道。",
  bindingFields: [
    { key: "account_level", label: "账户等级 account_level", placeholder: "留空 = 远端默认" },
    { key: "quota_limit", label: "额度 quota_limit", placeholder: "按额度(prepaid)渠道必填正数(支持小数)" },
    { key: "consumption_time_hours", label: "消耗周期 consumption_time_hours", type: "number", placeholder: "可选正整数(小时)" },
    {
      key: "access_test_model_mode",
      label: "测试模型模式 access_test_model_mode",
      type: "select",
      options: [
        { value: "", label: "inherit(默认，跟随渠道)" },
        { value: "inherit", label: "inherit(跟随渠道)" },
        { value: "custom", label: "custom(自定义模型)" },
      ],
    },
    { key: "access_test_model", label: "自定义测试模型 access_test_model", placeholder: "仅 custom 模式填写，如 gpt-5.6-luna" },
    { key: "supplier_remark", label: "供应商备注 supplier_remark", placeholder: "可选，最多 255 字符" },
  ],
  endpoints: [
    { idx: 0, m: "GET", path: AIXNET101_CHANNELS_PATH, hasBody: false, hasQuery: false, note: "查询当前 Supplier Token 可用的全部上号渠道" },
    { idx: 1, m: "GET", path: AIXNET101_BATCHES_PATH, hasBody: false, hasQuery: true, note: "批次历史(channel_id/page/page_size)" },
    { idx: 2, m: "POST", path: AIXNET101_BATCHES_PATH, hasBody: true, hasQuery: false, note: "异步提交 Key 批次(真实写操作 · 先把 channel_id 占位值换成渠道列表返回的数字；后端自动生成 Idempotency-Key)", mutating: true },
    { idx: 3, m: "GET", path: "/api/v1/openapi/supplier/key-batches/{batch_id}/", hasBody: false, hasQuery: false, note: "批次状态(需替换真实 batch_id)" },
    { idx: 4, m: "GET", path: "/api/v1/openapi/supplier/key-batches/{batch_id}/items/", hasBody: false, hasQuery: true, note: "批次逐条结果(page/page_size，需替换真实 batch_id)" },
    { idx: 5, m: "GET", path: "/api/v1/openapi/supplier/channels/{channel_id}/reconciliation/summary/", hasBody: false, hasQuery: false, note: "渠道对账汇总(需替换真实 channel_id)" },
    { idx: 6, m: "GET", path: AIXNET101_RECONCILIATION_PATH, hasBody: false, hasQuery: true, note: "渠道对账明细(batch_scope_key/page/page_size，需替换 channel_id)" },
    { idx: 7, m: "POST", path: AIXNET101_RECONCILIATION_PATH, hasBody: true, hasQuery: false, note: "按完整 API Key 精确查询对账明细(需替换 channel_id；Key 仅本次瞬时使用)", mutating: true },
  ],
  defaultBodies: AIXNET101_DEFAULT_BODIES,
  defaultQueries: AIXNET101_DEFAULT_QUERIES,
};

const FKAI_BASE_URL = "https://k.fkai.shop";

const FKAI_DEFAULT_BODIES: Record<string, string> = {
  "/api/uploads/import": JSON.stringify(
    {
      key_type: "oaix",
      credential_schema: "openai_bearer",
      keys_text: "REPLACE_WITH_UPSTREAM_KEY",
      levels_json: ["T2"],
      models_json: [["gpt-4o", "gpt-4o-mini"]],
      quota_amount: "20",
      quota_tokens_json: [null],
    },
    null,
    2,
  ),
  "/api/uploads/usage-lookup": JSON.stringify(
    { keys: ["REPLACE_WITH_UPSTREAM_KEY"], uploader_id: null, page: 1, page_size: 20 },
    null,
    2,
  ),
};

const FKAI_PROFILE: AdapterProfile = {
  label: "custom-k-fkai-shop · k.fkai.shop 推送站",
  optionLabel: "custom-k-fkai-shop(k.fkai.shop)",
  baseUrl: FKAI_BASE_URL,
  authHeaderLine: (hint) => `Authorization: Bearer <login JWT> (password hint: ${hint})`,
  hasRemoteTemplates: false,
  allowedAuthType: "basic",
  credentialFields: ["user_name", "password"],
  authFieldHint: "填 k.fkai.shop 供应商成员账号与密码。JWT 只缓存在后端进程内。",
  bindingSkeleton: [
    { key: "level", value: "T2" },
    { key: "quota_amount", value: "20" },
  ],
  bindingFields: [
    { key: "level", label: "等级 level", placeholder: "默认 T2" },
    { key: "quota_amount", label: "额度 quota_amount", type: "decimal", placeholder: "默认 20，必须为正数" },
  ],
  endpoints: [
    { idx: 0, m: "POST", path: "/api/auth/login", hasBody: false, hasQuery: false, note: "登录并隐藏 JWT" },
    { idx: 1, m: "POST", path: "/api/uploads/import", hasBody: true, hasQuery: false, note: "单 Key 导入，后端转为 multipart", mutating: true },
    { idx: 2, m: "POST", path: "/api/uploads/usage-lookup", hasBody: true, hasQuery: false, note: "按明文 Key 查询累计用量" },
  ],
  defaultBodies: FKAI_DEFAULT_BODIES,
  defaultQueries: {},
};

// custom-20218912178 是 newapi 系推送站的自建实例:
// 账号密码登录(后端持会话 Cookie)→ POST /api/channels?async=1 批量创建渠道
// → GET /api/usage-logs 查询近 30 天 quota(除以 quota_per_unit 得 USD)。
// remote_channel_id 取创建返回的 local_id;用量查询的 channel_id 与之相同。
const CUSTOM_20218912178_BASE_URL = "http://202.189.12.178:8095";

const CUSTOM_20218912178_PROFILE: AdapterProfile = {
  label: "custom-20218912178 · 推送站 202.189.12.178",
  optionLabel: "custom-20218912178(自建推送站)",
  baseUrl: CUSTOM_20218912178_BASE_URL,
  authHeaderLine: (hint) => `Cookie: aws_gateway_session=*** (user_name: ${hint})`,
  hasRemoteTemplates: false,
  allowedAuthType: "basic",
  credentialFields: ["user_name", "password"],
  probePath: "/api/login",
  authFieldHint:
    "账号 + 密码即推送站登录凭证(会话 Cookie 由后端持有,不落明文)。创建渠道前请在绑定行 field_mapping 配置 group(目标分组,如 openai)。",
  bindingSkeleton: [
    { key: "group", value: "" },
    { key: "billing_mode", value: "run" },
    { key: "quota_per_unit", value: "500000" },
  ],
  bindingFields: [
    {
      key: "group",
      label: "目标分组",
      placeholder: "必填,例如 openai",
      type: "text",
      required: true,
    },
    {
      key: "groups",
      label: "附加分组",
      placeholder: "可选,逗号分隔;留空=仅 group",
      type: "text",
    },
    {
      key: "name",
      label: "渠道名",
      placeholder: "留空=上游 Key 名称",
      type: "text",
    },
    {
      key: "type",
      label: "渠道类型",
      placeholder: "默认 1(OpenAI)",
      type: "number",
    },
    {
      key: "base_url",
      label: "上游 Base URL",
      placeholder: "留空=站点上游地址,再缺省 https://api.openai.com",
      type: "text",
    },
    {
      key: "priority",
      label: "优先级",
      placeholder: "默认 1",
      type: "number",
      visibleByDefault: true,
    },
    {
      key: "weight",
      label: "权重",
      placeholder: "默认 1",
      type: "number",
    },
    {
      key: "auto_ban",
      label: "自动禁用",
      placeholder: "默认 1(启用自动禁用)",
      type: "number",
    },
    {
      key: "test_model",
      label: "测试模型",
      placeholder: "留空=站点默认",
      type: "text",
    },
    {
      key: "tag",
      label: "标签",
      placeholder: "可选",
      type: "text",
    },
    {
      key: "remark",
      label: "备注",
      placeholder: "可选",
      type: "text",
    },
    {
      key: "model_scope",
      label: "模型范围",
      placeholder: "默认 none",
      type: "text",
    },
    {
      key: "proxy",
      label: "代理",
      placeholder: "可选",
      type: "text",
    },
    {
      key: "billing_mode",
      label: "计费方式",
      type: "select",
      visibleByDefault: true,
      options: [
        { value: "run", label: "run - 跑付" },
        { value: "buyout", label: "buyout - 买断" },
      ],
    },
    {
      key: "quota_per_unit",
      label: "花费换算：每美元额度",
      placeholder: "USD 花费 = quota ÷ 此值；默认 500000",
      type: "decimal",
    },
    {
      key: "usage_days",
      label: "用量回溯天数",
      placeholder: "固定 30 天；留空同样查询近 30 天",
      type: "number",
    },
  ],
  endpoints: [
    { idx: 0, m: "POST", path: "/api/login", hasBody: false, hasQuery: false, note: "登录连接测试" },
    { idx: 1, m: "GET", path: "/api/usage-logs", hasBody: false, hasQuery: true, note: "按渠道和时间窗口查询用量" },
    { idx: 2, m: "POST", path: "/api/channels", hasBody: true, hasQuery: true, note: "异步创建渠道", mutating: true },
  ],
  defaultBodies: {
    "/api/channels": JSON.stringify({ mode: "batch", channel: { name: "test", type: 1, key: "REPLACE_WITH_UPSTREAM_KEY", models: "", group: "", groups: [], base_url: "https://api.openai.com", priority: 1, weight: 1, auto_ban: 1, model_scope: "none" }, billing_mode: "run" }, null, 2),
  },
  defaultQueries: {
    "/api/usage-logs": JSON.stringify({ start_ts: "", end_ts: "", group: "", type: "0", page: "1", page_size: "20", channel_id: "" }, null, 2),
    "/api/channels": JSON.stringify({ async: "1" }, null, 2),
  },
};

// custom-10332033-xyz 是自研前后端分离推送站:
// 账号密码登录(后端持 token Cookie)→ POST /api/channels/batch-create 批量创建
// 渠道 → GET /api/allkeys/data 按单日(start=end=今天)拉全量、按 id 取 used_usd。
// remote_channel_id 取创建返回的 created[].id。
const CUSTOM_10332033XYZ_BASE_URL = "https://bls-report.10332033.xyz";

const CUSTOM_10332033XYZ_PROFILE: AdapterProfile = {
  label: "custom-10332033-xyz · 推送站 bls-report.10332033.xyz",
  optionLabel: "custom-10332033-xyz(自建推送站)",
  baseUrl: CUSTOM_10332033XYZ_BASE_URL,
  authHeaderLine: (hint) => `Cookie: token=*** (user_name: ${hint})`,
  hasRemoteTemplates: false,
  allowedAuthType: "basic",
  credentialFields: ["user_name", "password"],
  probePath: "/api/login",
  authFieldHint:
    "账号 + 密码即推送站登录凭证(登录换 token Cookie 由后端持有,不落明文)。创建渠道前请在绑定行 field_mapping 配置 group(目标分组)、quota_usd(渠道额度)与 suffix(渠道命名后缀,源站必填);type(主协议编号)由系统按 key 的原厂协议自动填充。",
  bindingSkeleton: [
    { key: "group", value: "" },
    { key: "quota_usd", value: "" },
    { key: "suffix", value: "" },
  ],
  bindingFields: [
    {
      key: "group",
      label: "目标分组",
      placeholder: "必填,逗号分隔,例如 openai,openai-alice",
      type: "text",
      required: true,
    },
    {
      key: "quota_usd",
      label: "渠道额度(USD)",
      placeholder: "必填,正数,例如 10",
      type: "decimal",
      required: true,
    },
    {
      key: "suffix",
      label: "渠道命名后缀",
      placeholder: "必填,站点渠道名 MMDD-studio-suffix-quota",
      type: "text",
      required: true,
    },
    {
      key: "priority",
      label: "优先级",
      placeholder: "可选,非负整数,组内排序(高者优先)",
      type: "number",
    },
    {
      key: "weight",
      label: "权重",
      placeholder: "可选,非负整数,同优先级带内权重",
      type: "number",
    },
  ],
  endpoints: [
    { idx: 0, m: "POST", path: "/api/login", hasBody: false, hasQuery: false, note: "登录连接测试" },
    { idx: 1, m: "GET", path: "/api/allkeys/data", hasBody: false, hasQuery: true, note: "按单日拉全量渠道用量" },
    { idx: 2, m: "POST", path: "/api/channels/batch-create", hasBody: true, hasQuery: false, note: "批量创建渠道", mutating: true },
  ],
  defaultBodies: {
    "/api/channels/batch-create": JSON.stringify({ studio: "", suffix: "", channels: [{ key: "REPLACE_WITH_UPSTREAM_KEY", quota_usd: 10 }], type: 1, group: "", models: "" }, null, 2),
  },
  defaultQueries: {
    "/api/allkeys/data": JSON.stringify({ start: "2026-10-29", end: "2026-10-29" }, null, 2),
  },
};

// withBindingFields derives the field_mapping whitelist from the friendly
// bindingFields specs, so the validator and the binding modal share one source.
function withBindingFields(profile: AdapterProfile): AdapterProfile {
  if (profile.fieldMappingKeys) return profile;
  const fields = profile.bindingFields ?? [];
  return {
    ...profile,
    fieldMappingKeys: fields.map((f) => ({
      key: f.key,
      label: f.label,
      required: f.required,
      visibleByDefault: f.visibleByDefault,
      type: f.type === "number" ? "int" : f.type === "decimal" ? "decimal" : f.type === "select" ? "enum" : "string",
      enum: f.options?.map((o) => o.value).filter((v) => v !== ""),
      enumLabels: f.options && Object.fromEntries(f.options.map((o) => [o.value, o.label])),
      hint: f.placeholder,
    })),
  };
}

export const ADAPTER_PROFILES: Record<string, AdapterProfile> = {
  "custom-ezlinkai-com": withWellKnownKeys(EZLINK_PROFILE),
  "custom-deepnix-ai": withWellKnownKeys(DEEPNIX_PROFILE),
  "custom-rocksoild-ai": withWellKnownKeys(ROCKSOILDAI_PROFILE),
  "custom-flypig": withWellKnownKeys(FLYPIG_PROFILE),
  "custom-aiabc-cfd": withWellKnownKeys(AIABC_PROFILE),
  "custom-ximeizhi-silicon-ovhish": withBindingFields(XIMEIZHI_PROFILE),
  "custom-rocketcloudai": withBindingFields(ROCKETCLOUD_PROFILE),
  "custom-ximeizi-lk999ai": withBindingFields(XIMEIZHI_LK999AI_PROFILE),
  "custom-101aixnet": withBindingFields(AIXNET101_PROFILE),
  "custom-k-fkai-shop": withBindingFields(FKAI_PROFILE),
  "custom-20218912178": withWellKnownKeys(withBindingFields(CUSTOM_20218912178_PROFILE)),
  "custom-10332033-xyz": withWellKnownKeys(withBindingFields(CUSTOM_10332033XYZ_PROFILE)),
};

// ADAPTER_CHOICES is the single source of truth for adapter_type values the
// create/update form will accept. `custom-push` is the always-available
// no-profile fallback (nop adapter). All other entries come from
// ADAPTER_PROFILES so adding a new merchant only needs one profile entry —
// dropdown, drawer, and row badge all pick it up automatically. The <select>
// input restricts operator input to these values; server-side
// validateFieldMapping() rejects anything else at write time.
export const ADAPTER_CHOICES: { value: string; label: string }[] = [
  { value: "custom-push", label: "custom-push(兜底)" },
  ...Object.entries(ADAPTER_PROFILES).map(([value, p]) => ({ value, label: p.optionLabel })),
];

// hasRemoteTemplates reports whether the given adapter_type has a "list remote
// templates" API worth exposing on /push-bindings. Unknown/unregistered
// adapters (including the `custom-push` fallback) default to false — the
// binding form then hides the 拉取/push_template_id controls, which is the
// safe default. The field_mapping editor itself stays available for ALL
// adapters (doc/011: every adapter ingests its site-scoped parameters from the
// binding row).
export function hasRemoteTemplates(adapterType: string): boolean {
  return ADAPTER_PROFILES[adapterType]?.hasRemoteTemplates === true;
}

// bindingSkeletonJson returns the pretty-printed field_mapping skeleton for an
// adapter, or null when the adapter declares no auto-prefill. Used by
// /push-bindings to prefill the mapping textarea for adapters with no remote
// templates — so the operator edits values instead of hand-typing JSON.
export function bindingSkeletonJson(adapterType: string): string | null {
  const sk = ADAPTER_PROFILES[adapterType]?.bindingSkeleton;
  if (!sk || sk.length === 0) return null;
  return JSON.stringify(sk, null, 2);
}

// requiredBindingKeysFor returns the fieldMappingKeys with `required: true`, in
// declaration order. Every consumption site (/push-bindings required hints,
// /push-sites + /distribute "binding missing key" badges) reads from this ONE
// derivation so the three pages cannot drift from each other (doc/041 §3.2).
export function requiredBindingKeysFor(adapterType: string): string[] {
  const keys = ADAPTER_PROFILES[adapterType]?.fieldMappingKeys;
  if (!keys) return [];
  return keys.filter((k) => k.required).map((k) => k.key);
}

// missingBindingKey returns the name of the first binding-row field_mapping
// key the given adapter REQUIRES to have a non-empty value but which the
// mapping currently lacks — or null when the adapter has no such requirement
// or the mapping satisfies it (an entry with a real, non-empty value, or the
// value is a legal JSON primitive such as 2 for channel_group_id). Unparseable
// mapping counts as missing every required key. /push-bindings uses the FIRST
// missing key as the required-field hint; /push-sites and /distribute use it
// to flag a binding that would fail CreateChannel pre-flight ("未发送:… is
// required") instead of a failed task discovered after the fact.
export function missingBindingKey(adapterType: string, fieldMappingJson: string): string | null {
  const required = requiredBindingKeysFor(adapterType);
  if (required.length === 0) return null;
  let entries: { key: string; value: string }[] = [];
  try {
    const parsed = JSON.parse(fieldMappingJson || "");
    if (Array.isArray(parsed)) entries = parsed;
  } catch {
    entries = [];
  }
  const map = new Map(entries.map((e) => [String(e?.key ?? ""), String(e?.value ?? "")]));
  for (const k of required) {
    const v = map.get(k);
    if (!v || v.trim() === "") return k;
  }
  return null;
}

// fieldMappingPlaceholder renders the shared field_mapping JSON example for
// the binding textarea's placeholder slot. Kept in the profile module so the
// operator always sees the same shape no matter which row/side they open.
// user_name removed — it now lives on the push_site row alongside the
// password (auth_type=basic).
export function fieldMappingPlaceholder(): string {
  return JSON.stringify(
    [
      { key: "platform_channel_type", value: "openai" },
      // NOTE: 值一律字符串(doc/042 §3.2)——后端 fieldMappingEntry.Value 是 string,
      // 写数字(如 value: 2)会让 decode 直接失败。示例即正确用法。
      { key: "channel_group_id", value: "2" },
      { key: "priority", value: "6" },
    ],
    null,
    2,
  );
}

// validateFieldMapping runs the pre-save frontend check (doc/041 §3.4 +
// doc/042 §4.4): JSON shape + required keys + int/decimal-value check +
// whitelist/duplicate warnings. Errors block a save; warnings are suggestions the
// operator can dismiss. Adapters without a fieldMappingKeys whitelist (e.g.
// custom-push fallback) get no whitelist warnings but still get the JSON-shape
// + required (none) checks.
export function validateFieldMapping(adapterType: string, json: string): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  const spec = ADAPTER_PROFILES[adapterType]?.fieldMappingKeys;
  const allowed = new Set(spec?.map((k) => k.key) ?? []);
  const specByKey = new Map((spec ?? []).map((k) => [k.key, k]));

  const trimmed = (json ?? "").trim();
  if (trimmed === "") {
    // Empty mapping is legal for adapters whose CreateChannel does not read
    // field_mapping (ezlink/custom-push). When required keys ARE declared,
    // empty mapping means every required key is missing — fail closed.
    const req = requiredBindingKeysFor(adapterType);
    if (req.length > 0) return { errors: [`缺少必填 key "${req[0]}"`], warnings };
    return { errors, warnings };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { errors: ["非法 JSON：无法解析"], warnings };
  }
  if (!Array.isArray(parsed)) {
    return { errors: ["必须是 [{key,value},...] 数组"], warnings };
  }

  const seen = new Map<string, number>();
  const requiredKeys = requiredBindingKeysFor(adapterType);
  const missingRequired = new Set(requiredKeys);
  for (const entry of parsed) {
    const key = String((entry as { key?: unknown } | null)?.key ?? "");
    if (key.trim() === "") {
      errors.push("存在空 key 的条目:每个元素必须含非空 key");
      continue;
    }
    missingRequired.delete(key);
    const rawValue = (entry as { value?: unknown } | null)?.value;
    if (rawValue === undefined || rawValue === null || String(rawValue).trim() === "") {
      errors.push(`key "${key}" 的 value 不能为空`);
    } else {
      if (spec && !allowed.has(key)) {
        warnings.push(`key "${key}" 不在该商家允许列表内(将被后端忽略)`);
      }
      // doc/042 §3.2:type="int" 键的值必须可被后端 strconv.Atoi 解析。前端先拦,
      // 不等“保存成功但分发时失败”。decimal 与 lk999 discount 的后端规则一致:
      // 最多两位小数(范围由后端最终校验)。
      const valueText = String(rawValue).trim();
      if (specByKey.get(key)?.type === "int" && !/^-?\d+$/.test(valueText)) {
        errors.push(`key "${key}" 必须是整数(当前 "${String(rawValue)}")`);
      }
      if (specByKey.get(key)?.type === "decimal" && !/^\d+(\.\d{1,2})?$/.test(valueText)) {
        errors.push(`key "${key}" 必须是数字,最多 2 位小数(当前 "${String(rawValue)}")`);
      }
    }
    const count = seen.get(key) ?? 0;
    seen.set(key, count + 1);
    if (seen.get(key) === 2) warnings.push(`key "${key}" 重复声明,后值覆盖前值`);
  }
  if (missingRequired.size > 0) {
    errors.push(`缺少必填 key "${Array.from(missingRequired)[0]}"`);
  }
  return { errors, warnings };
}
